import { createContext, useContext, useMemo } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { Contact } from '../ContactS';
import { useContact } from '../hooks/useContact';
import { openEditContactModal } from '../../Organismns/Contact/EditContactModal';
import { useModalState } from '../../Molecules/Dialogs';
import ContactDeleteModalFlow from '../../Organismns/Contact/ContactDeleteModalFlowDialog';
import { useUnreadMessagesCount } from '../hooks/useUnreadMessagesCount';
import { UnreadCountProps } from '../../Types/MailT';

interface Props {
  loading: boolean;
  contact: Contact | null;
  openContactModal: () => void;
  editContact: (patch: Partial<Contact>) => Promise<boolean>;
  removeContact: () => void;
  unreadMessagesProps: UnreadCountProps;
}

export const ContactDetailCtx = createContext({} as Props);

export const ContactDetailsProvider = () => {
  const { id: contactId } = useParams();
  const navigate = useNavigate();
  const { contact, loading, editContact, setContact } = useContact(contactId);
  const { show, toggle } = useModalState(false);
  const contactIds = useMemo(() => (contactId ? [Number(contactId)] : undefined), [contactId]);
  const { unreadCount, refresh, increment, decrement } = useUnreadMessagesCount(contactIds);
  const { personDescription, email } = contact ?? {};

  const openContactModal = () => {
    if (contact) {
      openEditContactModal(contact, undefined, setContact);
    }
  };

  const value = useMemo(
    () => ({
      contact,
      loading,
      openContactModal,
      editContact,
      removeContact: toggle,
      unreadMessagesProps: {
        increment,
        decrement,
        refresh,
        unreadCount,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contact, loading, unreadCount],
  );

  return (
    <ContactDetailCtx.Provider value={value}>
      {show && contactId && (
        <ContactDeleteModalFlow
          close={toggle}
          personDescription={personDescription || email || ''}
          contactId={contactId}
          reload={() => navigate('/contacts')}
        />
      )}
      <Outlet />
    </ContactDetailCtx.Provider>
  );
};

export const useContactDetails = () => useContext(ContactDetailCtx);
